/* eslint-disable max-lines */
import ApiService from '.';
import { CalendarEventLinkParams, CalendarMeetingParams, CreateNotificationConfirmationParams, CreateNotificationScheduledParams, SaveCustomHoursParams, UpdateCalendarLinkParams, UpdateHoursParams } from '../params/redux/calendar';
/**
 * @heading1: Integration API service.
 * @paragraph: This module provides a set of methods to interact with the Integration API.
 * It handles operations like Fetching folder details and create folder.
 */
const CalendarApi = {
    /**
     * @heading3: Fetch Folders
     * @paragraph: Sends a POST request to retrieve all folders available for the user.
     * @param: {string} token - The user's authentication token.
     * @param: {number} client_id - The parameters required id on which the folders will be fetch.
     * @returns: {Promise<unknown[]>} - The response containing a list of all folders.
     * @separator:
     */
    fetchEvents: (token: string, user_id: number) => {
        // Perform a POST request to fetch folders with the provided data
        return ApiService.apiHit({
            url: 'get-calendar',
            method: 'post',
            token,
            data: { user_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    /**
     * @heading3: Create Folder
     * @paragraph: Sends a POST request to Create a Folder.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @param: {string} folder_name - The folder_name containing the Name of folder for the operation.
     * @param: {number} client_id - The client_id containing location for the operation.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    createLink: (token: string, data: CalendarEventLinkParams) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'google-calendar/link-create',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    /**
     * @heading3: Delete Folder
     * @paragraph: Sends a POST request to Delete a Folder.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @param: {number} folder_id - The folder_id containing the id number of folder for the operation.
     * @param: {number} client_id - The client_id containing location for the operation.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    createMeeting: (token: string, data: CalendarMeetingParams) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'google-meetings/create',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    saveDays: (token: string, user_id: number) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'save-availability-days',
            method: 'post',
            token,
            data: { user_id },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    activeInactiveDay: (token: string, day_id: number, status: 'active' | 'inactive') => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'active-inactive-days',
            method: 'post',
            token,
            data: { day_id, status },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    fetchDays: (token: string, user_id: number) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'get-days-data',
            method: 'post',
            token,
            data: { user_id },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    updateTimes: (token: string, data: UpdateHoursParams) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'save-availability-hours',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    removeTime: (token: string, hour_id: number) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'delete-availability-hours',
            method: 'post',
            token,
            data: { hour_id },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    updateTimeZone: (token: string, timezone: string, user_id: number) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'update-time-zone',
            method: 'post',
            token,
            data: { timezone, user_id },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    updateCustomTimeOfDate: (token: string, data: SaveCustomHoursParams) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'save-custom-hours',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    fetchCustomTimeOfDate: (token: string, date: string, month: string, user_id: number) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'get-custom-hours',
            method: 'post',
            token,
            data: { date, month, user_id },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    fetchAllTimeZones: (token: string) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'get-time-zones',
            method: 'get',
            token
        });
    },
    fetchUserTimeZone: (token: string, user_id: number) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'get-user-time-zones',
            method: 'post',
            token,
            data: { user_id },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    enableDisabledCustomHour: (token: string, user_id: number, month: string, date: string, make_me_not_available: string) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'enable-disable-custom-hour',
            method: 'post',
            token,
            data: { user_id, month, date, make_me_not_available },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    updateUserTimeZone: (token: string, user_id: number, timezone: string) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'update-user-time-zone',
            method: 'post',
            token,
            data: { user_id, timezone },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    saveCalendarMeetingFormJSON: (token: string, calendar_id: number, form_json: string) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'update-calendar-meeting-form-json',
            method: 'post',
            token,
            data: { calendar_id, form_json },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    fetchCalendarLinkNotificaiton: (token: string, calendar_id: number) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'get-calendar-notification',
            method: 'post',
            token,
            data: { calendar_id },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
     createCalendarLinkNotificationConfirmation: (token: string, data: CreateNotificationConfirmationParams) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'create-calendar-notification-confirmation',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
     createCalendarLinkNotificationScheduling: (token: string, data: CreateNotificationScheduledParams) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'create-calendar-notification-scheduling',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    deleteCalendarLinkNotification: (token: string, calendar_id: number, notification_id: number) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'delete-calendar-notification',
            method: 'post',
            token,
            data: { calendar_id, notification_id },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    fetchMeetingNotificaitons: (token: string, event_id: number) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'get-event-notification',
            method: 'post',
            token,
            data: { event_id },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    createMeetingNotificationConfirmation: (token: string, data: CreateNotificationConfirmationParams) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'create-event-notification-confirmation',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    createMeetingNotificationScheduling: (token: string, data: CreateNotificationScheduledParams) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'create-event-notification-scheduling',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    deleteMeetingNotificaion: (token: string, event_id: number, notification_id: number) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'delete-event-notification',
            method: 'post',
            token,
            data: { event_id, notification_id },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    editCalendarLink: (token: string, data: UpdateCalendarLinkParams) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'update-calendar-link',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    deleteCalendarLink: (token: string, calendar_id: number) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'delete-calendar-link',
            method: 'post',
            token,
            data: { calendar_id },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    deleteMeeting: (token: string, calendar_meeting_id: number) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'delete-calendar-meeting',
            method: 'post',
            token,
            data: { calendar_meeting_id },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    fetchCalendarReminders: (token: string, client_id: number) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'get-calendar-reminders',
            method: 'post',
            token,
            data: { client_id },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    },
    fetchRecentMeetings: (token: string) => {
        // Perform a Post request to create folder endpoint with the token
        return ApiService.apiHit({
            url: 'get-recent-meetings',
            method: 'post',
            token,
            data: { },
            getIp: {
                get: true,
                fieldValue: 'created_ip'
            }
        });
    }
};

export default CalendarApi;
